<template>
  <v-sheet outlined rounded class="pa-3 mb-3" data-testid="grid_sequence_completion_condition_edit">
    <template v-if="value.type === 'always'">
      <v-alert dense outlined type="info" class="mb-0">
        {{ $t("grid_sequence_completion_condition.completes_automatically") }}
      </v-alert>
      <v-btn v-if="!readonly && condition_options_available" small color="primary" class="mt-3" @click="start_conditions">
        {{ $t("grid_sequence_completion_condition.add_completion_conditions") }}
      </v-btn>
    </template>

    <template v-else-if="value.type === 'group'">
      <v-row align="center" dense>
        <v-col cols="12" sm="5">
          <v-select
            :value="value.operator"
            :items="group_operators"
            :label="$t('grid_sequence_completion_condition.group_rule')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_group_rule"
            @change="set_property('operator', $event)"
          />
        </v-col>
        <v-col v-if="value.operator !== 'never'" class="text-right">
          <v-btn
            v-if="!readonly"
            small
            text
            color="primary"
            data-testid="grid_sequence_completion_condition_edit_add_condition"
            @click="add_condition"
          >
            {{ $t("grid_sequence_completion_condition.add_condition") }}
          </v-btn>
          <v-btn
            v-if="!readonly"
            small
            text
            color="primary"
            data-testid="grid_sequence_completion_condition_edit_add_group"
            @click="add_group"
          >
            {{ $t("grid_sequence_completion_condition.add_group") }}
          </v-btn>
          <v-btn
            v-if="removable && !readonly"
            small
            icon
            color="red"
            data-testid="grid_sequence_completion_condition_edit_remove"
            @click="$emit('remove')"
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-alert v-if="value.operator === 'never'" dense outlined type="info" class="mt-3 mb-0">
        {{ $t("grid_sequence_completion_condition.never_transition_description") }}
      </v-alert>
      <div
        v-for="(condition, index) in value.operator === 'never' ? [] : value.conditions"
        :key="index"
        class="ml-4 mt-3"
      >
        <GridSequenceCompletionConditionEdit
          :value="condition"
          :fields="fields"
          :completed_exercises="completed_exercises"
          :readonly="readonly"
          :top_level="false"
          removable
          @input="replace_condition(index, $event)"
          @remove="remove_condition(index)"
        />
      </div>
      <v-alert
        v-if="value.operator !== 'never' && value.conditions.length === 0"
        dense
        outlined
        type="warning"
        class="mt-3 mb-0"
      >
        {{ $t("grid_sequence_completion_condition.add_comparison_or_group") }}
      </v-alert>
    </template>

    <template v-else-if="value.type === 'exercise_completed_count'">
      <v-row dense align="center">
        <v-col cols="12" md="7">
          <v-select
            :value="value.exercise_id"
            :items="condition_options"
            :label="$t('grid_sequence_completion_condition.exercise')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_condition_type"
            @change="condition_changed"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            :value="value.count"
            type="number"
            min="1"
            step="1"
            :label="$t('grid_sequence_completion_condition.completion_count')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_completion_count"
            @input="positive_integer_changed('count', $event)"
          />
        </v-col>
        <v-col cols="auto">
          <v-btn
            v-if="removable && !readonly"
            icon
            color="red"
            data-testid="grid_sequence_completion_condition_edit_remove"
            @click="$emit('remove')"
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <template v-else-if="value.type === 'step_live_days'">
      <v-row dense align="center">
        <v-col cols="12" md="7">
          <v-select
            value="__step_live_days"
            :items="condition_options"
            :label="$t('grid_sequence_completion_condition.condition_type')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_condition_type"
            @change="condition_changed"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            :value="value.days"
            type="number"
            min="1"
            step="1"
            :label="$t('grid_sequence_completion_condition.live_days')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_live_days"
            @input="positive_integer_changed('days', $event)"
          />
        </v-col>
        <v-col cols="auto">
          <v-btn
            v-if="removable && !readonly"
            icon
            color="red"
            data-testid="grid_sequence_completion_condition_edit_remove"
            @click="$emit('remove')"
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <v-row dense align="center">
        <v-col cols="12" md="6">
          <v-select
            :value="value.field"
            :items="condition_options"
            :label="$t('grid_sequence_completion_condition.reported_value')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_condition_type"
            @change="condition_changed"
          />
        </v-col>
        <v-col v-if="value.field_type !== 'boolean'" cols="12" md="2">
          <v-select
            :value="value.operator"
            :items="operator_options"
            :label="$t('grid_sequence_completion_condition.comparison')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_comparison"
            @change="set_property('operator', $event)"
          />
        </v-col>
        <v-col v-if="value.field_type === 'label' || value.field_type === 'multi_select'" cols="12" md="3">
          <v-select
            :value="value.value"
            :items="label_values"
            :multiple="['in', 'contains_any', 'contains_all'].includes(value.operator)"
            :label="$t('grid_sequence_completion_condition.value')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_value"
            @change="set_property('value', $event)"
          />
        </v-col>
        <v-col v-else-if="value.field_type === 'boolean'" cols="12" md="3">
          <v-select
            :value="value.value"
            :items="boolean_values"
            outlined
            dense
            :label="$t('grid_sequence_completion_condition.value')"
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_value"
            @change="set_property('value', $event)"
          />
        </v-col>
        <v-col v-else-if="value.field_type === 'text'" cols="12" md="3">
          <v-text-field
            :value="value.value"
            :label="$t('grid_sequence_completion_condition.value')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            data-testid="grid_sequence_completion_condition_edit_value"
            @input="set_property('value', $event)"
          />
        </v-col>
        <v-col v-else-if="value.field_type === 'date'" cols="12" md="3">
          <v-text-field
            :value="value.value"
            type="date"
            :label="$t('grid_sequence_completion_condition.date')"
            dense
            outlined
            hide-details
            :disabled="readonly"
            @input="set_property('value', $event)"
          />
        </v-col>
        <v-col v-else cols="12" md="3">
          <v-text-field
            :value="display_value"
            type="number"
            :step="value.field_type === 'numeric' ? 'any' : 1"
            :label="value_label"
            dense
            outlined
            hide-details
            :disabled="readonly"
            @input="numeric_value_changed"
          />
        </v-col>
        <v-col cols="auto" class="ml-auto text-right">
          <v-btn
            v-if="removable && !readonly"
            icon
            color="red"
            data-testid="grid_sequence_completion_condition_edit_remove"
            @click="$emit('remove')"
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <div v-if="field_hint" class="caption grey--text mt-2">{{ field_hint }}</div>
    </template>
  </v-sheet>
</template>

<script>
import Vue from "vue";
import {
  comparisonOperatorOptions,
  defaultComparison,
  reportingFieldType,
} from "../classes/grid_sequence";

export default {
  name: "GridSequenceCompletionConditionEdit",
  props: {
    value: { type: Object, required: true },
    fields: { type: Object, required: true },
    completed_exercises: { type: Array, required: true },
    removable: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    top_level: { type: Boolean, default: true },
  },
  computed: {
    group_operators() {
      const operators = [
        { text: this.$t("grid_sequence_completion_condition.match_all"), value: "match_all" },
        { text: this.$t("grid_sequence_completion_condition.match_any"), value: "match_any" },
      ];
      if (this.top_level) {
        operators.push({
          text: this.$t("grid_sequence_completion_condition.never_transition"),
          value: "never",
        });
      }
      return operators;
    },
    field_options() {
      return Object.keys(this.fields).map((field) => ({
        text: this.localized(this.fields[field].title) || field,
        value: field,
        type: "comparison",
      }));
    },
    condition_options() {
      const options = [];
      const usedFields = new Set();
      this.completed_exercises.forEach((exercise) => {
        options.push({
          text: `${exercise.text}: ${this.$t("grid_sequence_completion_condition.exercise_completed_count")}`,
          value: exercise.value,
          type: "exercise_completed_count",
        });
        this.field_options
          .filter((field) => field.value.startsWith(`${exercise.value}.`))
          .forEach((field) => {
            options.push(field);
            usedFields.add(field.value);
          });
      });
      this.field_options.filter((field) => !usedFields.has(field.value)).forEach((field) => options.push(field));
      options.push({
        text: this.$t("grid_sequence_completion_condition.step_live_days"),
        value: "__step_live_days",
        type: "step_live_days",
      });
      return options;
    },
    condition_options_available() {
      return true;
    },
    field_config() {
      return this.fields[this.value.field] || {};
    },
    operator_options() {
      return comparisonOperatorOptions(this.value.field_type, this.$t.bind(this));
    },
    label_values() {
      if (this.field_config.options) return this.field_config.options;
      const labels = this.field_config.value_labels || {};
      return Object.keys(labels).map((value) => ({
        text: this.localized(labels[value]) || value,
        value: this.typed_label_value(value),
      }));
    },
    boolean_values() {
      return [
        { text: this.$t("common.yes"), value: true },
        { text: this.$t("common.no"), value: false },
      ];
    },
    display_value() {
      if (this.value.field_type === "percent") {
        return Number(this.value.value || 0) * 100;
      }
      if (this.value.field_type === "duration") {
        return Number(this.value.value || 0) / 1000;
      }
      return this.value.value;
    },
    value_label() {
      if (this.value.field_type === "percent") return this.$t("grid_sequence_completion_condition.percent");
      if (this.value.field_type === "duration") return this.$t("grid_sequence_completion_condition.seconds");
      return this.$t("grid_sequence_completion_condition.value");
    },
    field_hint() {
      const display = this.field_config.display || "";
      const suffix = this.localized(this.field_config.suffix);
      if (display.startsWith("vergence_")) {
        return this.$t("grid_sequence_completion_condition.vergence_hint", {
          convention: display.replace("vergence_", "").toUpperCase(),
        });
      }
      if (display === "phoria_horizontal") return this.$t("grid_sequence_completion_condition.phoria_horizontal_hint");
      if (display === "phoria_vertical") return this.$t("grid_sequence_completion_condition.phoria_vertical_hint");
      if (["fraction", "fraction_with_percent"].includes(display)) {
        if (this.field_config.fraction_denominator) {
          return this.$t("grid_sequence_completion_condition.fraction_hint_with_denominator", {
            denominator: this.field_config.fraction_denominator,
          });
        }
        return this.$t("grid_sequence_completion_condition.fraction_hint");
      }
      return suffix || "";
    },
  },
  methods: {
    localized(values) {
      if (!values) return "";
      if (typeof values === "string") return values;
      return values[this.$i18n.lang()] || values.en || "";
    },
    typed_label_value(value) {
      if (value === "true") return true;
      if (value === "false") return false;
      if (/^-?\d+(\.\d+)?$/.test(value)) return Number(value);
      return value;
    },
    set_property(property, value) {
      const updated = { ...this.value, [property]: value };
      if (property === "operator" && value === "never") updated.conditions = [];
      const arrayOperators = ["in", "contains_any", "contains_all"];
      if (property === "operator" && arrayOperators.includes(value) && !Array.isArray(updated.value)) {
        updated.value = updated.value === "" ? [] : [updated.value];
      }
      if (property === "operator" && !arrayOperators.includes(value) && Array.isArray(updated.value)) {
        updated.value = updated.value[0] ?? "";
      }
      this.$emit("input", updated);
    },
    field_type(display) {
      return reportingFieldType(display);
    },
    field_changed(field) {
      this.$emit("input", defaultComparison(field, this.fields[field]));
    },
    condition_changed(value) {
      const option = this.condition_options.find((candidate) => candidate.value === value);
      if (!option) return;
      if (option.type === "exercise_completed_count") {
        this.$emit("input", { type: "exercise_completed_count", exercise_id: value, count: 1 });
        return;
      }
      if (option.type === "step_live_days") {
        this.$emit("input", { type: "step_live_days", days: 1 });
        return;
      }
      this.field_changed(value);
    },
    numeric_value_changed(value) {
      let normalized = Number(value);
      if (this.value.field_type === "percent") normalized /= 100;
      if (this.value.field_type === "duration") normalized *= 1000;
      this.set_property("value", normalized);
    },
    positive_integer_changed(property, value) {
      this.set_property(property, Number(value));
    },
    new_condition() {
      const option = this.condition_options[0];
      if (!option) return null;
      if (option.type === "exercise_completed_count") {
        return { type: "exercise_completed_count", exercise_id: option.value, count: 1 };
      }
      if (option.type === "step_live_days") {
        return { type: "step_live_days", days: 1 };
      }
      return defaultComparison(option.value, this.fields[option.value]);
    },
    add_condition() {
      const condition = this.new_condition();
      if (!condition) return;
      const conditions = this.value.conditions.slice();
      conditions.push(condition);
      this.set_property("conditions", conditions);
    },
    start_conditions() {
      const condition = this.new_condition();
      if (!condition) return;
      this.$emit("input", { type: "group", operator: "match_all", conditions: [condition] });
    },
    add_group() {
      const conditions = this.value.conditions.slice();
      conditions.push({ type: "group", operator: "match_all", conditions: [] });
      this.set_property("conditions", conditions);
    },
    replace_condition(index, condition) {
      const conditions = this.value.conditions.slice();
      Vue.set(conditions, index, condition);
      this.set_property("conditions", conditions);
    },
    remove_condition(index) {
      const conditions = this.value.conditions.slice();
      conditions.splice(index, 1);
      this.set_property("conditions", conditions);
    },
  },
};
</script>
